import React, { useState, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { Headline, Button, useFlyout } from 'msp-components'
import {
  useWindowSize,
  useConfig,
  useNavigation,
  useBraze
} from 'msp-integrations'
import {
  ContentCardBannerProps,
  ValidColor,
  validColors
} from './ContentCardBanner.types'
import { IconButton } from '@/components/molecules/IconButton'

const defaultColor = 'green'

const imagePosition = {
  left: 'object-left',
  center: 'object-center',
  right: 'object-right',
  top: 'object-top',
  bottom: 'object-bottom'
}

const margins = {
  category_hero: 'mb-20 lg:mb-32',
  small: 'mb-16 mt-4',
  medium: 'mb-20 mt-8',
  large: 'mb-24 mt-12',
  none: 'mb-12'
}

const ContentCardBanner = ({
  className,
  cards = []
}: ContentCardBannerProps) => {
  const imgRef = React.createRef<HTMLImageElement>()
  const navigate = useNavigation()
  const [xSlidePos, setXSlidePos] = useState<number>(0)
  const [isSliding, setIsSliding] = useState<boolean>(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const { openFlyout } = useFlyout()
  const { termsConditionsCards } = useBraze()

  const [loaded, setLoaded] = useState(false)
  const [displaySrc, setDisplaySrc] = useState('')
  const { width, breakpoints } = useWindowSize()
  const isDesktop = width && width >= breakpoints.lg
  const {
    theme: {
      font: { regular: fontRegular },
      mspComponents: { cardBanner: bannerTheme }
    }
  } = useConfig()

  const color = cards[currentIndex]?.extras?.color
  const currentColor: ValidColor = validColors.includes(color as ValidColor)
    ? (color as ValidColor)
    : defaultColor

  useEffect(() => {
    setCurrentIndex(0)
  }, [cards?.length])

  useEffect(() => {
    if (cards[currentIndex]?.imageUrl !== displaySrc && imgRef?.current) {
      setLoaded(false)
      const src = cards[currentIndex]?.imageUrl
      imgRef.current.src = src
      imgRef.current.onload = () => {
        setDisplaySrc(src)
        setLoaded(true)
      }
    }
  }, [cards[currentIndex]?.imageUrl])

  const prev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? cards.length - 1 : prevIndex - 1
    )
  }

  const next = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === cards.length - 1 ? 0 : prevIndex + 1
    )
  }

  const handleSlideStart = (newXPos: number) => {
    setXSlidePos(newXPos)
    setIsSliding(true)
  }

  const handleSlide = (newXPos: number) => {
    if (xSlidePos > newXPos && isSliding) {
      next()
    } else if (xSlidePos < newXPos && isSliding) {
      prev()
    }
    setIsSliding(false)
  }

  const handleInfoClick = (termId?: string) => {
    if (termId && termsConditionsCards) {
      const termsCard = termsConditionsCards.get(termId)
      if (termsCard) {
        openFlyout(termsCard.title, termsCard.description)
      }
    }
  }

  if (!cards.length) return null

  return (
    <div className={className}>
      <div
        className={twMerge(
          'h-[469px] w-full px-5 py-6 lg:h-[577px] lg:px-14 lg:py-12',
          margins[cards[0].extras?.margin_type || 'category_hero'],
          'transition-colors duration-700 ease-in-out',
          fontRegular,
          bannerTheme[currentColor].backgroundColor,
          bannerTheme[currentColor].fontColor,
          'stroke-[4px]'
        )}
        onTouchStart={(ev) =>
          !isDesktop && handleSlideStart(ev.changedTouches[0].pageX)
        }
        onTouchMove={(ev) =>
          !isDesktop && handleSlide(ev.changedTouches[0].pageX)
        }
      >
        <div
          className={twMerge(
            'flex h-[485px] flex-col items-center overflow-hidden text-center lg:h-[577px] lg:flex-row-reverse',
            bannerTheme[currentColor].foregroundColor
          )}
        >
          <img
            ref={imgRef}
            src={cards[currentIndex]?.imageUrl}
            alt={cards[currentIndex]?.title}
            loading='lazy'
            className={twMerge(
              'aspect-[16/9] h-52 w-full object-cover lg:h-full lg:w-2/3',
              imagePosition[
                cards[currentIndex]?.extras?.image_position || 'top'
              ],
              'transition-opacity duration-500 ease-in-out',
              loaded ? 'opacity-100' : 'opacity-0'
            )}
          />
          <div
            className={twMerge(
              'relative flex h-full w-full flex-col justify-between px-6 pb-6 pt-4 lg:h-full lg:w-1/3 lg:px-12 lg:py-10'
            )}
          >
            <div>
              {cards[currentIndex]?.title && (
                <Headline
                  tag='h3'
                  className={twMerge(
                    bannerTheme[currentColor].fontColor,
                    'transition-colors duration-700 ease-in-out'
                  )}
                >
                  {cards[currentIndex].title}
                </Headline>
              )}
              {cards[currentIndex]?.extras?.terms_id && (
                <IconButton
                  iconName='InformationNoPadding'
                  className={twMerge(
                    'flex-shrink-0 lg:mt-2',
                    bannerTheme[currentColor].iconColor,
                    'transition-colors duration-700 ease-in-out'
                  )}
                  width='24px'
                  height='24px'
                  onClick={() =>
                    handleInfoClick(cards[currentIndex]?.extras?.terms_id)
                  }
                />
              )}
              {cards[currentIndex]?.description && (
                <p
                  className='text-16 lg:text-18 xl:text-24 leading-24 lg:leading-36 text-left font-normal lg:mt-2'
                  dangerouslySetInnerHTML={{
                    __html: cards[currentIndex].description.replace(
                      /\r\n/g,
                      '<br/>'
                    )
                  }}
                />
              )}
              {cards[currentIndex]?.url && cards[currentIndex]?.linkText && (
                <Button
                  className={twMerge(
                    bannerTheme[currentColor].foregroundColor,
                    'mt-2 md:mt-4 xl:mt-6'
                  )}
                  variant={'outline'}
                  type={'primary'}
                  isDarkMode={currentColor === 'green'}
                  onClick={() => navigate(cards[currentIndex].url)}
                >
                  {cards[currentIndex].linkText}
                </Button>
              )}
            </div>

            {cards.length > 1 && (
              <div className='flex h-auto flex-row items-center justify-center align-bottom md:justify-start'>
                <IconButton
                  className={twMerge(
                    bannerTheme[
                      cards[currentIndex].extras?.color || defaultColor
                    ]?.btnOutline
                  )}
                  iconName='ArrowLeft'
                  width='26px'
                  height='26px'
                  onClick={prev}
                />
                <IconButton
                  className={twMerge(
                    bannerTheme[
                      cards[currentIndex].extras?.color || defaultColor
                    ]?.btnOutline,
                    'ml-6'
                  )}
                  iconName='ArrowRight'
                  width='26px'
                  height='26px'
                  onClick={next}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentCardBanner
